import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { saveComment, unsaveComment, getSavedComments } from '../../pages/auth/utils/firebase/userOperations';

// Thunks
export const fetchSavedComments = createAsyncThunk(
  'savedComments/fetchSavedComments',
  async (_, { rejectWithValue }) => {
    try {
      const comments = await getSavedComments();
      return comments;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveCommentToLocal = createAsyncThunk(
  'savedComments/saveComment',
  async (comment, { dispatch, rejectWithValue }) => {
    try {
      // optimistic add
      dispatch(addSavedComment(comment));
      await saveComment(comment);
      return comment;
    } catch (error) {
      // revert optimistic add
      dispatch(removeSavedComment({ id: comment.id }));
      return rejectWithValue(error.message);
    }
  }
);

export const unsaveCommentFromLocal = createAsyncThunk(
  'savedComments/unsaveComment',
  async (commentId, { getState, dispatch, rejectWithValue }) => {
    const original = getState()?.savedComments?.items?.find((c) => c.id === commentId);
    // optimistic remove
    dispatch(removeSavedComment({ id: commentId }));
    try {
      await unsaveComment(commentId);
      return commentId;
    } catch (error) {
      // revert on failure
      if (original) {
        dispatch(addSavedComment(original));
      }
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  hasFetched: false,
};

const savedCommentsSlice = createSlice({
  name: 'savedComments',
  initialState,
  reducers: {
    addSavedComment: (state, action) => {
      const c = action.payload;
      if (!state.items.some((x) => x.id === c.id)) {
        state.items.push(c);
      }
    },
    removeSavedComment: (state, action) => {
      const { id } = action.payload;
      state.items = state.items.filter((c) => c.id !== id);
    },
    clearSavedCommentsState: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedComments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
        state.hasFetched = true;
      })
      .addCase(fetchSavedComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load saved comments';
      })
      .addCase(saveCommentToLocal.pending, (state) => {
        // avoid global loading flicker
        state.error = null;
      })
      .addCase(saveCommentToLocal.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(unsaveCommentFromLocal.pending, (state) => {
        // avoid global loading flicker
        state.error = null;
      })
      .addCase(unsaveCommentFromLocal.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { addSavedComment, removeSavedComment, clearSavedCommentsState } = savedCommentsSlice.actions;
export default savedCommentsSlice.reducer;
